import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, AlertTriangle, Package, CheckCircle2, Clock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/efs/PageHeader';
import { KgCard } from '@/components/efs/KgCard';
import { ExceptionSeverityGrid } from '@/components/efs/ExceptionSeverityGrid';
import { ProgressCard } from '@/components/efs/ProgressCard';
import { BatchStatusBadge } from '@/components/efs/BatchStatusBadge';
import type { BatchStatus } from '@/components/efs/BatchStatusBadge';
import { useBatchContext } from '@/app/BatchContext';
import { useDashboard } from './useDashboard';

const NS = 'common';

const ReconciliationStatusBadge = ({ status }: { status: string | null }) => {
  const { t } = useTranslation(NS);
  if (!status) return <span className="text-sm text-efs-text-muted">—</span>;

  const toneClass =
    status === 'blocked'
      ? 'efs-bg-critical'
      : status === 'ready_with_warnings'
        ? 'efs-bg-warning'
        : status === 'ready'
          ? 'efs-bg-success'
          : 'bg-efs-bg-subtle text-efs-text-muted';

  const label = t(`efs.dashboard.reconciliation.${status}`, { defaultValue: status });

  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium uppercase tracking-wide ${toneClass}`}
    >
      {label}
    </span>
  );
};

export const DashboardPage = () => {
  const { t } = useTranslation(NS);
  const navigate = useNavigate();
  const { batchId, setBatchId } = useBatchContext();

  const { data, isLoading, isError, isForbidden, isNotFound } = useDashboard(batchId ?? undefined);

  if (!batchId) {
    return (
      <PageHeader
        title={t('efs.common.nav.dashboard')}
        subtitle={t('efs.common.messages.no_batch_selected')}
      />
    );
  }

  // P1-#7: surface a recoverable UI when the selected batch was deleted or
  // the current user lost access. 404 already triggered a `setBatchId(null)`
  // inside the hook so the next render falls through to the no-batch view;
  // 403 we keep the batch selected so the user understands which batch.
  if (isForbidden) {
    return (
      <div className="space-y-4">
        <PageHeader title={t('efs.common.nav.dashboard')} />
        <Card>
          <CardContent className="px-4 py-6 space-y-3">
            <p className="text-sm text-efs-text-muted">
              {t('efs.dashboard.messages.batch_forbidden')}
            </p>
            <Button size="sm" variant="outline" onClick={() => setBatchId(null)}>
              {t('efs.dashboard.actions.select_another_batch')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isNotFound) {
    return (
      <PageHeader
        title={t('efs.common.nav.dashboard')}
        subtitle={t('efs.dashboard.messages.batch_unavailable')}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader title={t('efs.common.nav.dashboard')} />

      {isLoading ? (
        <p className="text-sm text-efs-text-muted">{t('efs.common.messages.loading')}</p>
      ) : isError || !data ? (
        <p className="text-sm text-efs-text-muted">{t('efs.common.messages.empty')}</p>
      ) : (
        <>
          {/* Row 1: Batch status + reconciliation status */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-xs font-medium text-efs-text-muted uppercase tracking-wide">
                  {t('efs.dashboard.widgets.batch_status')}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <BatchStatusBadge status={data.batch_status as BatchStatus} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-xs font-medium text-efs-text-muted uppercase tracking-wide">
                  {t('efs.dashboard.widgets.reconciliation')}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <ReconciliationStatusBadge status={data.reconciliation_status} />
              </CardContent>
            </Card>

            {/* Pending approvals */}
            <Card>
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-xs font-medium text-efs-text-muted uppercase tracking-wide">
                  {t('efs.dashboard.widgets.approvals')}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <div className="flex items-center gap-4 text-sm">
                  <span>
                    <span className="font-semibold tabular-nums">
                      {data.pending_approvals.master_changes}
                    </span>{' '}
                    <span className="text-efs-text-muted">
                      {t('efs.dashboard.approvals.master')}
                    </span>
                  </span>
                  <span>
                    <span className="font-semibold tabular-nums">
                      {data.pending_approvals.exceptions}
                    </span>{' '}
                    <span className="text-efs-text-muted">
                      {t('efs.dashboard.approvals.exceptions')}
                    </span>
                  </span>
                  <span>
                    <span className="font-semibold tabular-nums">
                      {data.pending_approvals.monthly_close}
                    </span>{' '}
                    <span className="text-efs-text-muted">
                      {t('efs.dashboard.approvals.close')}
                    </span>
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Kg totals */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KgCard label={t('efs.dashboard.widgets.inbound')} value={data.totals.inbound_kg} />
            <KgCard label={t('efs.dashboard.widgets.accounted')} value={data.totals.accounted_kg} />
            <KgCard
              label={t('efs.dashboard.widgets.diff')}
              value={data.totals.diff_kg}
              tone={
                parseFloat(data.totals.diff_kg) === 0
                  ? 'success'
                  : parseFloat(data.totals.diff_kg) > 0
                    ? 'warning'
                    : 'error'
              }
            />
            <Card>
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-xs font-medium text-efs-text-muted uppercase tracking-wide">
                  {t('efs.dashboard.widgets.diff_percent')}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <span className="text-2xl font-semibold font-mono tabular-nums">
                  {data.totals.diff_percent}%
                </span>
              </CardContent>
            </Card>
          </div>

          {/* Row 3: Exceptions + Evidence + Package */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <ExceptionSeverityGrid
              counts={data.open_exceptions}
              title={t('efs.dashboard.widgets.exceptions')}
            />

            <ProgressCard
              label={t('efs.dashboard.widgets.evidence')}
              uploaded={data.evidence_completeness.uploaded}
              required={
                data.evidence_completeness.required > 0
                  ? data.evidence_completeness.required
                  : data.evidence_completeness.uploaded
              }
            />

            <Card>
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-xs font-medium text-efs-text-muted uppercase tracking-wide">
                  {t('efs.dashboard.widgets.package')}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3 space-y-1">
                {data.package_status.latest_version != null ? (
                  <p className="text-sm">
                    {t('efs.dashboard.package.version', {
                      version: data.package_status.latest_version,
                    })}
                  </p>
                ) : (
                  <p className="text-sm text-efs-text-muted">{t('efs.dashboard.package.none')}</p>
                )}
                {data.package_status.can_generate ? (
                  <Link to={`/batches/${batchId}/audit-package`}>
                    <Button size="sm" variant="outline" className="mt-1">
                      <Package className="mr-1 h-3 w-3" />
                      {t('efs.dashboard.actions.generate_package')}
                    </Button>
                  </Link>
                ) : null}
              </CardContent>
            </Card>
          </div>

          {/* Primary actions panel */}
          <Card>
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm font-medium">
                {t('efs.dashboard.actions.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3 flex flex-wrap gap-2">
              <Link to={`/batches/${batchId}`}>
                <Button variant="outline" size="sm">
                  <ArrowRight className="mr-1 h-4 w-4" />
                  {t('efs.dashboard.actions.open_batch')}
                </Button>
              </Link>

              <Button
                variant="default"
                size="sm"
                onClick={() => navigate(`/batches/${batchId}/mass-balance`)}
              >
                <CheckCircle2 className="mr-1 h-4 w-4" />
                {t('efs.dashboard.actions.run_reconciliation')}
              </Button>

              {data.open_exceptions.critical > 0 || data.open_exceptions.error > 0 ? (
                <Link to={`/batches/${batchId}/exceptions`}>
                  <Button variant="destructive" size="sm">
                    <AlertTriangle className="mr-1 h-4 w-4" />
                    {t('efs.dashboard.actions.view_blockers')}
                  </Button>
                </Link>
              ) : null}

              {data.pending_approvals.monthly_close > 0 ? (
                <Link to="/approvals">
                  <Button variant="outline" size="sm">
                    <Clock className="mr-1 h-4 w-4" />
                    {t('efs.dashboard.actions.pending_approvals')}
                  </Button>
                </Link>
              ) : null}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
